import type { AppError } from "@/shared/api";

/** Канал показу: інлайн у шіті vs глобальний toast. */
export type BookingErrorSurface = "inline" | "toast";

/**
 * Типобезпечні i18n-ключі помилок журналу.
 * Доменні `journal.errors.*` + generic `errors.*`.
 */
export type BookingErrorKey =
  | "journal.errors.slotTaken"
  | "journal.errors.concurrentModification"
  | "journal.errors.outsideWorkingHours"
  | "journal.errors.notEditable"
  | "journal.errors.serviceNotConfigured"
  | "journal.errors.invalidPhone"
  | "journal.errors.invalidClientName"
  | "journal.errors.notFound"
  | "errors.forbidden"
  | "errors.rateLimited"
  | "errors.server"
  | "errors.network"
  | "errors.unknown";

export interface MappedBookingError {
  surface: BookingErrorSurface;
  i18nKey: BookingErrorKey;
  toastVariant: "destructive" | "info";
  /** Чи потрібно перезавантажити доступні слоти (після slot_taken). */
  reloadAvailability?: boolean;
}

/**
 * Адаптер `AppError → MappedBookingError` (патерн mapServiceError).
 * Матриця §8 spec:
 *
 * - 409 slot_taken → inline + reloadAvailability
 * - 409 concurrent_modification → inline
 * - 422 outside_working_hours → inline
 * - 422 booking_not_editable → inline
 * - 422 master_service_not_configured → inline
 * - 422 invalid_phone → inline
 * - 422 invalid_client_name → inline
 * - 404 → toast(info)
 * - решта → toast(destructive) generic
 */
export function mapBookingError(e: AppError): MappedBookingError {
  switch (e.kind) {
    case "conflict":
      if (e.code === "slot_taken") {
        return {
          surface: "inline",
          i18nKey: "journal.errors.slotTaken",
          toastVariant: "destructive",
          reloadAvailability: true,
        };
      }
      if (e.code === "concurrent_modification") {
        return {
          surface: "inline",
          i18nKey: "journal.errors.concurrentModification",
          toastVariant: "destructive",
        };
      }
      return {
        surface: "toast",
        i18nKey: "errors.unknown",
        toastVariant: "destructive",
      };
    case "validation":
      if (e.code === "outside_working_hours") {
        return {
          surface: "inline",
          i18nKey: "journal.errors.outsideWorkingHours",
          toastVariant: "destructive",
        };
      }
      if (e.code === "booking_not_editable") {
        return {
          surface: "inline",
          i18nKey: "journal.errors.notEditable",
          toastVariant: "destructive",
        };
      }
      if (e.code === "master_service_not_configured") {
        return {
          surface: "inline",
          i18nKey: "journal.errors.serviceNotConfigured",
          toastVariant: "destructive",
        };
      }
      if (e.code === "invalid_phone") {
        return {
          surface: "inline",
          i18nKey: "journal.errors.invalidPhone",
          toastVariant: "destructive",
        };
      }
      if (e.code === "invalid_client_name") {
        return {
          surface: "inline",
          i18nKey: "journal.errors.invalidClientName",
          toastVariant: "destructive",
        };
      }
      return {
        surface: "toast",
        i18nKey: "errors.unknown",
        toastVariant: "destructive",
      };
    case "notFound":
      return {
        surface: "toast",
        i18nKey: "journal.errors.notFound",
        toastVariant: "info",
      };
    case "forbidden":
      return { surface: "toast", i18nKey: "errors.forbidden", toastVariant: "destructive" };
    case "rateLimited":
      return { surface: "toast", i18nKey: "errors.rateLimited", toastVariant: "destructive" };
    case "network":
      return { surface: "toast", i18nKey: "errors.network", toastVariant: "destructive" };
    case "server":
      return { surface: "toast", i18nKey: "errors.server", toastVariant: "destructive" };
    default:
      return { surface: "toast", i18nKey: "errors.unknown", toastVariant: "destructive" };
  }
}
